import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCaseTableColumnNames,
  fetchAllCases,
  addCase,
  updateCase,
  updateCaseJson,
  deleteCases,
  fetchAllCaseTypes,
  uploadCaseFile
} from "./thunk";
import { toast } from "react-toastify";

const initialState = {
  tableColumnNames: [],
  cases: [], 
  headerLoading: false,
  loading: false, 
  caseTypes: [],
  caseTypesLoading: false,
  error: null,
  uploadProgress: 0,
  totalRecords: 0,
  caseUploading: false,
  uploadedCaseResponse: null
};

const CasesSlice = createSlice({
  name: "Cases",
  initialState,
  reducers: {
    getCases: (state, action) => {
      const data = action.payload.Data;

      // Step 1: Find deleted documents
      const deletedIDs = new Set(
        data.filter((item) => item.IsDelete).map((item) => item.ID)
      );

      // Step 2: Remove deleted docs from existing state
      const updatedCases = state.cases.filter(
        (doc) => !deletedIDs.has(doc.ID)
      );

      // Step 3: Continue your original merge logic (for non-deleted docs)
      const activeData = data.filter((item) => !item.IsDelete);
      const caseObj = Object.fromEntries(activeData.map((item) => [item.ID, item]));
      const existingIDs = new Set(updatedCases.map((d) => d.ID));

      state.cases = [
        ...activeData.filter((item) => !existingIDs.has(item.ID)),
        ...updatedCases.map((item) => caseObj[item.ID] ?? item),
      ];
    },
    updateCaseProcessingStatus: (state, action) => {
      const { ID, Status, JsonURL, ModifiedByID } = action.payload;
      state.cases = state.cases.map((item) =>
        item.ID == ID ? { ...item, Status, JsonURL, ModifiedByID } : item    
      );
    },
    setCaseUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    setCaseUploadError: (state, action) => {
      state.error = action.payload; 
    },
    clearCaseUploadError(state) {
      state.error = null;
    },
    clearUploadedCaseResponse: (state) => {
      state.caseUploading = false;
      state.uploadedCaseResponse = null;
    },
    clearCases: (state) => {
      state.cases = [];
      state.totalRecords = 0;
    }
  },
  extraReducers: (builder) => {
    builder

      // Upload Case File API
      .addCase(uploadCaseFile.pending, (state) => {
        state.caseUploading = true;
        state.uploadedCaseResponse = null;
      })
      .addCase(uploadCaseFile.fulfilled, (state, action) => {
        state.caseUploading = false;
        state.uploadedCaseResponse = action.payload;
      })
      .addCase(uploadCaseFile.rejected, (state) => {        
        state.caseUploading = false;
      })

      // Fetch Document Table Column Names API
      .addCase(fetchCaseTableColumnNames.pending, (state) => {
        state.headerLoading = true;
        state.error = null;
      })
      .addCase(fetchCaseTableColumnNames.fulfilled, (state, action) => {
        state.headerLoading = false;
        state.tableColumnNames = action.payload;
      })
      .addCase(fetchCaseTableColumnNames.rejected, (state, action) => {
        state.headerLoading = false;
        state.error = action.payload;
      })

      // Fetch All Cases
      .addCase(fetchAllCases.pending, (state) => {
        // state.loading = true;
        state.error = null;
      })
      // .addCase(fetchAllCases.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.cases = [...state.cases, ...action.payload];
      // })
      .addCase(fetchAllCases.fulfilled, (state, action) => {
        // state.loading = false;

        // Merge old and new data
        const mergedCases = [...state.cases, ...action.payload.data];
        state.totalRecords = action.payload.totalRecords;
        const uniqueCases = Array.from(
          new Map(mergedCases.map(item => [item.ID, item])).values()
        );

        state.cases = uniqueCases;
      })
      .addCase(fetchAllCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(typeof action.payload === 'string' ? action.payload : "Failed to fetch case.");
      })

      .addCase(addCase.pending, (state) => {
        state.error = null;
      })
      .addCase(addCase.fulfilled, (state, action) => {
        state.cases = [...action.payload, state.cases];
      })
      .addCase(addCase.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(typeof action.payload === 'string' ? action.payload : "Failed to save case.");
      })

      .addCase(updateCase.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCase.fulfilled, (state, action) => {
        state.cases = state.cases.map((c) => c.ID == action.payload.ID ? 
          action.payload :
          c
        );
      })
      .addCase(updateCase.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(typeof action.payload === 'string' ? action.payload : "Failed to save case.");
      })

      .addCase(updateCaseJson.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCaseJson.fulfilled, (state, action) => {
        state.cases = state.cases.map((c) => c.ID == action.payload.ID ? 
          action.payload :
          c
        );
      })
      .addCase(updateCaseJson.rejected, (state, action) => {
        state.error = action.payload;
        toast.error(typeof action.payload === 'string' ? action.payload : "Failed to save case.");
      })

      // Delete Document
      .addCase(deleteCases.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteCases.fulfilled, (state, action) => {
        const deletedIds = action.payload.map((item) => item.ID);

        // Filter out the deleted items
        state.cases = state.cases.filter((item) => !deletedIds.includes(item.ID));

      })
      .addCase(deleteCases.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Get Case Types
      .addCase(fetchAllCaseTypes.pending, (state) => {
        state.caseTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCaseTypes.fulfilled, (state, action) => {
        state.caseTypesLoading = false;
        state.caseTypes = action.payload;
      })
      .addCase(fetchAllCaseTypes.rejected, (state, action) => {
        state.caseTypesLoading = false;
        state.error = action.payload;
      })
  },
});

export const { getCases, updateCaseProcessingStatus, setCaseUploadProgress, setCaseUploadError, clearCaseUploadError, clearUploadedCaseResponse, clearCases } = CasesSlice.actions;
export default CasesSlice.reducer;
